/**
 * Neura design tokens.
 *
 * Ported verbatim from the design system's single source of truth:
 *   NeuraDesigns/assets/neura.css  ->  `:root`
 *
 * That stylesheet is linked by 115 of the 126 mockups and carries explicit
 * "MUI equivalent:" notes — it was authored to be lifted into this stack.
 * When a mockup's inline <style> disagrees with a value here, this file wins:
 * the inline blocks are page-level overrides, not the system.
 *
 * `cssVariables` is the one source. The named constants below are derived from
 * it rather than restated, so the CSS custom properties and the JS values can
 * never drift apart. Consume them two ways:
 *   - import the named constants   (the MUI theme, and `sx` where a JS value
 *                                   is needed — e.g. composing a gradient)
 *   - `var(--accent)`              (anywhere in `sx`, plain CSS, or a rule
 *                                   lifted verbatim from a mockup)
 *
 * Deliberately omitted from the port:
 *   --rfi*        design-review annotation chrome, not product UI
 *   --fab-gutter  reserves space for the mockups' floating Design Mode toggle,
 *                 which does not ship
 */

export const cssVariables = {
  // Surfaces
  "--bg": "#0D1117",
  "--surface": "#161B22",
  "--surface-2": "#1F262F",

  // Lines
  "--border": "rgba(255, 255, 255, 0.06)",
  "--border-strong": "rgba(255, 255, 255, 0.12)",

  // Type
  "--text": "#ffffff",
  "--text-secondary": "rgba(255, 255, 255, 0.65)",
  "--text-muted": "rgba(255, 255, 255, 0.5)",
  "--text-faint": "rgba(255, 255, 255, 0.35)",

  // Brand
  "--accent": "#FDB940",
  "--accent-2": "#E0A020",
  "--accent-tint": "rgba(253, 185, 64, 0.12)",
  "--accent-tint-strong": "rgba(253, 185, 64, 0.22)",

  // Status. Note there is no separate warning token in the system —
  // amber warning is expressed with the accent gold itself.
  "--red": "#EF4444",
  "--red-tint": "rgba(239, 68, 68, 0.12)",
  "--green": "#22C55E",
  "--green-tint": "rgba(34, 197, 94, 0.12)",
  "--blue": "#3B82F6",

  // Geometry
  "--radius-sm": "6px",
  "--radius-md": "8px",
  "--radius-lg": "10px",
  "--radius-xl": "12px",
  "--shadow-pop": "0 10px 30px rgba(0, 0, 0, 0.45)",
  "--ease-out": "cubic-bezier(0.4, 0, 0.2, 1)",

  // Type stacks
  "--font-stack": "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  "--font-mono": "'JetBrains Mono', 'SF Mono', Menlo, monospace",

  // Spacing — 4px base
  "--space-1": "4px",
  "--space-2": "8px",
  "--space-3": "12px",
  "--space-4": "16px",
  "--space-5": "20px",
  "--space-6": "24px",
  "--space-7": "32px",
  "--space-8": "48px",
  "--space-9": "64px",

  // Page container
  "--container-max": "1600px",
  "--container-pad-x": "32px",
  "--container-pad-y": "24px",

  // Top app bar
  "--header-h": "60px",
  "--header-pad-x": "20px",
  "--header-logo-h": "24px",

  // Collapsible sidebar
  "--drawer-w": "280px",
  "--sidebar-w": "280px",
  "--sidebar-w-collapsed": "64px",

  // Shared mobile breakpoint
  "--bp-mobile": "720px",
};

/** Renders `cssVariables` as the body of a `:root { ... }` rule. */
export const rootCssText = Object.entries(cssVariables)
  .map(([name, value]) => `  ${name}: ${value};`)
  .join("\n");

// Surfaces
export const BG = cssVariables["--bg"];
export const SURFACE = cssVariables["--surface"];
export const SURFACE_2 = cssVariables["--surface-2"];

// Lines
export const BORDER = cssVariables["--border"];
export const BORDER_STRONG = cssVariables["--border-strong"];

// Type
export const TEXT = cssVariables["--text"];
export const TEXT_SECONDARY = cssVariables["--text-secondary"];
export const TEXT_MUTED = cssVariables["--text-muted"];
export const TEXT_FAINT = cssVariables["--text-faint"];

// Brand
export const ACCENT = cssVariables["--accent"];
export const ACCENT_2 = cssVariables["--accent-2"];
export const ACCENT_TINT = cssVariables["--accent-tint"];
export const ACCENT_TINT_STRONG = cssVariables["--accent-tint-strong"];

// Status
export const RED = cssVariables["--red"];
export const RED_TINT = cssVariables["--red-tint"];
export const GREEN = cssVariables["--green"];
export const GREEN_TINT = cssVariables["--green-tint"];
export const BLUE = cssVariables["--blue"];

// Geometry
export const RADIUS_SM = cssVariables["--radius-sm"];
export const RADIUS_MD = cssVariables["--radius-md"];
export const RADIUS_LG = cssVariables["--radius-lg"];
export const RADIUS_XL = cssVariables["--radius-xl"];
export const SHADOW_POP = cssVariables["--shadow-pop"];
export const EASE_OUT = cssVariables["--ease-out"];

// Type stacks
export const FONT_STACK = cssVariables["--font-stack"];
export const FONT_MONO = cssVariables["--font-mono"];

/**
 * Composites the system uses often enough to be worth naming once.
 * The gradient is the single primary-action treatment across every app.
 */
export const ACCENT_GRADIENT = `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT_2} 100%)`;
export const FOCUS_RING = `0 0 0 3px rgba(253, 185, 64, 0.10)`;
export const FOCUS_BORDER = `rgba(253, 185, 64, 0.45)`;
